import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { Observable, from, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

// The bootstrap script calls this back once the API is ready; it is deleted
// again as soon as it fires so nothing else can reach into the global scope.
const callbackName = "__quotissGoogleMapsLoaded";

@Injectable({ providedIn: "root" })
export class GoogleMapsService {
  private api: Promise<void>;

  // Kept as an observable because callers gate a map, or an autocomplete, on
  // it. Resolves with `google.maps` core (Map, LatLngBounds, event) in place.
  load(): Observable<boolean> {
    return from(this.loadLibrary("maps")).pipe(
      map(() => true),
      catchError((err) => {
        console.error(err);
        return of(false);
      }),
    );
  }

  // Libraries are pulled on demand instead of through `libraries=` on the
  // script URL: that is what the `loading=async` bootstrap expects, and a
  // screen that only draws a map never pays for `places`.
  async loadLibrary<K extends keyof google.maps.ImportLibraryMap>(
    name: K,
  ): Promise<google.maps.ImportLibraryMap[K]> {
    await this.loadApi();

    return google.maps.importLibrary(name);
  }

  private loadApi(): Promise<void> {
    if (!this.api) {
      this.api = new Promise<void>((resolve, reject) => {
        const globals = window as unknown as Record<string, () => void>;
        globals[callbackName] = () => {
          delete globals[callbackName];
          resolve();
        };

        const params = new URLSearchParams({
          key: environment.googleApiKey,
          v: "weekly",
          // Without it the API logs a deprecation notice and loads the whole
          // library set synchronously.
          loading: "async",
          callback: callbackName,
        });

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
        script.async = true;
        script.onerror = () => {
          delete globals[callbackName];
          this.api = undefined;
          reject(new Error("Google Maps API could not be loaded"));
        };

        document.head.appendChild(script);
      });
    }

    return this.api;
  }
}
